import { useEffect, useState } from 'react';
import { AdminLayout } from '@/components/AdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { api } from '@/lib/api';
import { Loader2, CalendarCheck, XCircle, Users, GraduationCap, MessageSquare, Coins, CheckCircle2, Clock, UserX, CalendarClock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { formatDateTime } from '@/lib/format';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { canViewSub } from '@/lib/auth';

const kpiCards = (data) => [
    { sub: 'stat_upcoming', icon: CalendarCheck, label: 'Upcoming bookings', value: data.upcoming_bookings, hue: 'text-primary' },
    { sub: 'stat_completed', icon: CheckCircle2, label: 'Completed', value: data.completed_bookings, hue: 'text-emerald-600' },
    { sub: 'stat_mentor_cancelled', icon: UserX, label: 'Mentor cancelled', value: data.mentor_cancelled_bookings, hue: 'text-destructive' },
    { sub: 'stat_candidate_cancelled', icon: XCircle, label: 'Candidate cancelled', value: data.candidate_cancelled_bookings, hue: 'text-destructive' },
    { sub: 'stat_mentor_rescheduled', icon: CalendarClock, label: 'Mentor rescheduled', value: data.mentor_rescheduled_bookings, hue: 'text-amber-600' },
    { sub: 'stat_candidate_rescheduled', icon: CalendarClock, label: 'Candidate rescheduled', value: data.candidate_rescheduled_bookings, hue: 'text-amber-600' },
    { sub: 'stat_students', icon: Users, label: 'Students', value: data.total_students, hue: 'text-sky-700' },
    { sub: 'stat_credits', icon: Coins, label: 'Credits outstanding', value: data.total_credits_outstanding, hue: 'text-amber-600' },
    { sub: 'stat_mentors', icon: GraduationCap, label: 'Mentors connected', value: `${data.mentors_connected}/${data.total_mentors}`, hue: 'text-primary' },
    { sub: 'stat_feedback', icon: MessageSquare, label: 'Feedback submissions', value: data.feedback_submissions, hue: 'text-primary' },
].filter((c) => canViewSub('overview', c.sub));

export default function AdminOverviewPage() {
    const [data, setData] = useState(null);
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const [ov, bk] = await Promise.all([
                    api.get('/admin/overview'),
                    api.get('/admin/bookings', { params: { status: 'scheduled' } }),
                ]);
                setData(ov.data);
                setBookings((bk.data.bookings || []).slice(0, 5));
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    return (
        <AdminLayout title="Overview" subtitle="Snapshot of your booking platform">
            {loading || !data ? (
                <div className="flex items-center gap-2 rounded-lg border border-border/60 bg-muted/40 p-4 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                </div>
            ) : (
                <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                        {kpiCards(data).map((c) => (
                            <Card key={c.sub} className="card-lift" data-testid={`overview-kpi-${c.sub}`}>
                                <CardContent className="flex items-center gap-3 p-5">
                                    <span className={`inline-flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 ${c.hue}`}>
                                        <c.icon className="h-5 w-5" />
                                    </span>
                                    <div className="min-w-0">
                                        <div className="text-xs uppercase tracking-wide text-muted-foreground">{c.label}</div>
                                        <div className="font-display text-2xl font-semibold tabular-nums">{c.value}</div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>

                    {canViewSub('overview', 'upcoming_list') && (
                    <Card>
                        <CardContent className="p-6">
                            <div className="mb-4 flex items-center justify-between">
                                <div>
                                    <h2 className="font-display text-lg font-semibold">Next 5 upcoming sessions</h2>
                                    <p className="text-sm text-muted-foreground">Real-time from mentors’ calendars.</p>
                                </div>
                                <Button variant="secondary" size="sm" asChild>
                                    <Link to="/admin/bookings">View all</Link>
                                </Button>
                            </div>
                            {bookings.length === 0 ? (
                                <div className="rounded-xl border border-dashed border-border/60 bg-card p-8 text-center text-sm text-muted-foreground">
                                    No upcoming sessions yet.
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {bookings.map((b) => {
                                        const initials = b.mentor_name?.split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();
                                        return (
                                            <div key={b.id} className="flex items-center justify-between rounded-lg border border-border/60 bg-card p-3">
                                                <div className="flex items-center gap-3">
                                                    <Avatar className="h-9 w-9">
                                                        <AvatarImage src={b.mentor_avatar_url} />
                                                        <AvatarFallback className="bg-primary/10 text-primary text-xs">{initials}</AvatarFallback>
                                                    </Avatar>
                                                    <div>
                                                        <div className="text-sm font-medium">{b.mentor_name} · <span className="text-muted-foreground">{b.student_name}</span></div>
                                                        <div className="text-xs text-muted-foreground"><Clock className="mr-1 inline h-3 w-3" />{formatDateTime(b.start_iso)}</div>
                                                    </div>
                                                </div>
                                                <Badge>{b.duration_minutes} min</Badge>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                    )}
                </div>
            )}
        </AdminLayout>
    );
}
